import { useEffect, useState, type FC, type FormEvent } from "react";
import {
  Alert,
  AlertTitle,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { useMutation } from "@apollo/client";
import { useSnackbar } from "notistack";
import { CreateProductFeedbackMutation } from "@/entities/products";
import { useTranslation, useUser } from "@/shared/hooks";
import type {
  CreateProductFeedbackMutationType,
  CreateProductFeedbackMutationVariablesType,
} from "@/types/graphql/site.generated";

// Та же граница, что и в схеме бэкенда (PRODUCT_FEEDBACK_MESSAGE_MAX_LENGTH):
// лучше не дать набрать лишнее, чем показать ошибку после отправки.
const MAX_MESSAGE_LENGTH = 2000;

/**
 * Порт референсного arena/components/ProductFeedbackForm: одно поле и
 * кнопка, после успеха вместо формы Alert с адресом, на который придёт
 * ответ. Отличие от референса: форма живёт в собственном диалоге, а не
 * внутри справочного (у витрины его нет).
 */
export const ProductFeedbackDialog: FC<{
  productId: string;
  open: boolean;
  onClose: () => void;
}> = ({ productId, open, onClose }) => {
  const { t } = useTranslation();
  const { user } = useUser();
  const { enqueueSnackbar } = useSnackbar();
  const [message, setMessage] = useState("");
  const [sent, setSent] = useState(false);

  // Диалог остаётся смонтированным между открытиями, поэтому состояние
  // сбрасываем явно: иначе второй раз он открылся бы с "уже отправлено".
  useEffect(() => {
    if (open) {
      setMessage("");
      setSent(false);
    }
  }, [open]);

  const [createProductFeedback, { loading }] = useMutation<
    CreateProductFeedbackMutationType,
    CreateProductFeedbackMutationVariablesType
  >(CreateProductFeedbackMutation, {
    onCompleted: () => setSent(true),
    onError: (error) => enqueueSnackbar(error.message, { variant: "error" }),
  });

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    void createProductFeedback({
      variables: { input: { productId, message: message.trim() } },
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("productFeedback.dialogTitle")}</DialogTitle>

      {sent ? (
        <>
          <DialogContent>
            <Alert severity="success">
              <AlertTitle>{t("productFeedback.successTitle")}</AlertTitle>
              {t("productFeedback.successText", { email: user?.email ?? "" })}
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose} variant="contained">
              {t("productFeedback.cancel")}
            </Button>
          </DialogActions>
        </>
      ) : (
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              autoFocus
              fullWidth
              multiline
              minRows={4}
              maxRows={12}
              variant="outlined"
              label={t("productFeedback.message")}
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              slotProps={{ htmlInput: { maxLength: MAX_MESSAGE_LENGTH } }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose} color="inherit">
              {t("productFeedback.cancel")}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading || !message.trim()}
            >
              {t("productFeedback.submit")}
            </Button>
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
};
